import React, { useEffect, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const Hero: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shouldReduceMotion = useReducedMotion();
  const [playBackground, setPlayBackground] = useState(false);
  const [videoStarted, setVideoStarted] = useState(false);

  useEffect(() => {
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (shouldReduceMotion || !window.matchMedia("(min-width: 768px)").matches || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || "")) return;
    let timer: ReturnType<typeof setTimeout>;
    const start = () => { timer = setTimeout(() => setPlayBackground(true), 1500); };
    if (document.readyState === "complete") start();
    else window.addEventListener("load", start, { once: true });
    return () => { clearTimeout(timer); window.removeEventListener("load", start); };
  }, [shouldReduceMotion]);

  const { scrollY } = useScroll();
  const backgroundY = useTransform(scrollY, [0, 1000], ["0%", "24%"]);
  const backgroundOpacity = useTransform(scrollY, [0, 700], [1, 0.15]);

  const scrollToExplore = () => {
    window.scrollTo({
      top: window.innerHeight * 0.88,
      behavior: "smooth",
    });
  };

  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden flex flex-col justify-between px-4 sm:px-6 md:px-8 lg:px-12 pt-32 sm:pt-36 md:pt-40 pb-8 sm:pb-10">
      {/* ====================================================
          BACKGROUND VIDEO & CINEMATIC OVERLAYS
      ==================================================== */}
      <motion.div
        className="absolute inset-0 z-0 overflow-hidden pointer-events-none bg-black"
        style={{
          y: shouldReduceMotion ? "0%" : backgroundY,
          opacity: shouldReduceMotion ? 1 : backgroundOpacity,
        }}
      >
        <picture>
          {/* Same pixels and object-cover framing: omit only the offscreen sides. */}
          <source
            media="(max-width: 767px) and (max-aspect-ratio: 5/9)"
            srcSet="/hero-poster-portrait.webp"
            width={600}
            height={1080}
          />
          <img
            src="/hero-poster.webp"
            alt=""
            aria-hidden="true"
            width={1920}
            height={1080}
            fetchPriority="high"
            style={{ visibility: videoStarted ? "hidden" : "visible" }}
            className="absolute inset-0 w-full h-full object-cover opacity-100 scale-[1.02]"
          />
        </picture>
        {playBackground && (
          <video
            src="/bg.webm"
            poster="/hero-poster.webp"
            autoPlay muted loop playsInline aria-hidden="true"
            onPlaying={() => setVideoStarted(true)}
            onError={() => {
              setVideoStarted(false);
              setPlayBackground(false);
            }}
            className={`absolute inset-0 w-full h-full object-cover scale-[1.02] ${videoStarted ? "opacity-100" : "opacity-0"}`}
          />
        )}

        {/* Ambient Overlays */}
        <div className="absolute inset-0 bg-black/5 z-[1]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-transparent to-black/30 z-[1]" />
        <div
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 50%, transparent 55%, rgba(0,0,0,0.12) 100%)",
          }}
        />
      </motion.div>

      {/* ====================================================
          GOLD AMBIENT HALOS
      ==================================================== */}
      <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden" aria-hidden="true">
        <motion.div
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 2.4, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bottom-[-10%] left-[-10%] w-[60vw] h-[55vh] max-w-[850px] max-h-[850px] bg-[#a26850]/12 rounded-tr-[14rem] blur-2xl md:blur-3xl"
        />
      </div>

      {/* ====================================================
          HERO MAIN CONTENT
      ==================================================== */}
      <div className="relative z-10 max-w-[1440px] mx-auto w-full my-auto flex flex-col justify-center">
        {/* ====================================================
            HARMONIOUS ARCHITECTURAL TITLE LOCKUP
        ==================================================== */}
        <h1 className="relative flex flex-col font-oswald uppercase tracking-[-0.035em] select-none">
          {/* LINE 1: DEM */}
          <motion.span
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
            style={{ willChange: "transform, opacity" }}
            className="block font-bold text-white text-[clamp(2.75rem,8.2vw,9.75rem)] leading-[0.95] pb-1 drop-shadow-[0_20px_50px_rgba(0,0,0,0.65)]"
          >
            DEM
          </motion.span>

          {/* LINE 2: PROPERTIES — darker gold start, room under descenders */}
          <motion.span
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.05, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            style={{ willChange: "transform, opacity" }}
            className="block font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#A47C3B] via-[#a26850] to-[#6F5327] text-[clamp(2.75rem,8.2vw,9.75rem)] leading-[0.95] pb-2 sm:pb-3 mt-1 sm:mt-2 md:mt-3 lg:mt-3.5 pl-0 sm:pl-6 md:pl-10 lg:pl-14 xl:pl-16 drop-shadow-[0_20px_50px_rgba(0,0,0,0.45)]"
          >
            {t("hero.brand")}
          </motion.span>
        </h1>

        {/* ====================================================
            EDITORIAL SUBTITLE & CTA ACTIONS
        ==================================================== */}
        <div className="mt-8 sm:mt-10 md:mt-12 lg:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          {/* Left Column: Subtitle & Prestige Locations */}
          <div className="lg:col-span-7 flex flex-col gap-5 sm:gap-6">
            <motion.p
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-playfair text-lg sm:text-xl md:text-2xl text-stone-200/90 leading-relaxed italic border-l-2 border-[#a26850] pl-5 sm:pl-6 max-w-2xl"
            >
              {t("hero.subtitle")}
            </motion.p>

            {/* Micro Location tags */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.75, duration: 0.8 }}
              className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1.5 text-[11px] sm:text-xs font-oswald uppercase tracking-[0.25em] text-[#E6D2B5]/75 pl-5 sm:pl-6"
            >
              <span>Golden Mile</span>
              <span className="text-[#a26850]">·</span>
              <span>La Zagaleta</span>
              <span className="text-[#a26850]">·</span>
              <span>Sierra Blanca</span>
              <span className="text-[#a26850]">·</span>
              <span>Nueva Andalucía</span>
            </motion.div>
          </div>

          {/* Right Column: CTA Buttons */}
          <div className="lg:col-span-5 flex flex-col gap-4 lg:items-end">
            <motion.div
              initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col sm:flex-row gap-4 sm:gap-5 w-full sm:w-auto"
            >
              <button
                type="button"
                onClick={() => navigate("/properties")}
                className="group relative px-8 sm:px-9 py-4 sm:py-4.5 bg-black/45 hover:bg-[#a26850]/25 backdrop-blur-md text-white font-oswald font-normal text-xs uppercase tracking-[0.3em] overflow-hidden border border-white/25 hover:border-[#DFBA73] transition-all duration-500 shadow-[0_4px_25px_rgba(0,0,0,0.35)] hover:shadow-[0_0_25px_rgba(223,186,115,0.25)] flex items-center justify-center"
              >
                <span className="relative z-10 flex items-center gap-3 transition-all">
                  {t("hero.ctaProperties")}
                  <ArrowRight className="w-4 h-4 text-[#DFBA73] opacity-80 group-hover:opacity-100 group-hover:translate-x-1.5 transition-all duration-500" />
                </span>
              </button>

              <button
                type="button"
                onClick={() => navigate("/contact")}
                className="px-8 sm:px-9 py-4 sm:py-4.5 border border-transparent text-stone-300 hover:text-white font-oswald font-normal text-xs uppercase tracking-[0.3em] transition-colors duration-500 relative after:absolute after:bottom-3 after:left-8 after:right-8 after:h-[1px] after:bg-white/30 hover:after:bg-[#DFBA73] after:transition-colors flex items-center justify-center text-center"
              >
                {t("hero.ctaContact")}
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* ====================================================
          BOTTOM UTILITY BAR (SIGNATURE & SCROLL CUE)
      ==================================================== */}
      <div className="relative z-10 max-w-[1440px] mx-auto w-full pt-6 flex items-center justify-between border-t border-white/10 text-white/40">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="font-oswald text-[11px] sm:text-xs uppercase tracking-[0.3em] text-stone-400/80"
        >
          {t("hero.signature")}
        </motion.span>

        <motion.button
          type="button"
          onClick={scrollToExplore}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="hidden sm:flex items-center gap-3 group cursor-pointer bg-transparent border-0 p-0 text-left focus:outline-none"
          aria-label={t("hero.scroll", "Scroll")}
        >
          <span className="font-oswald text-[10px] sm:text-[11px] uppercase tracking-[0.3em] text-stone-400/70 group-hover:text-[#DFBA73] transition-colors">
            {t("hero.scroll", "Scroll")}
          </span>
          <div className="w-[1px] h-6 bg-gradient-to-b from-white/40 via-white/20 to-transparent group-hover:from-[#DFBA73] group-hover:h-8 transition-all duration-300" />
        </motion.button>
      </div>
    </section>
  );
};

export default Hero;
